<script setup>
import { ref, watch } from 'vue';
import { Head, usePage } from '@inertiajs/vue3';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { fr } from 'yup-locales';
import axios from 'axios';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import FloatLabel from 'primevue/floatlabel';
import InputMask from 'primevue/inputmask';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import Gtc from '@/Components/Gtc.vue';

const page = usePage();

yup.setLocale(fr);

const toast = useToast();

const isLoggedInUser = !!page.props.auth.user;
const isExistingUser = ref(isLoggedInUser);

const regularUnitPrice = Number(page.props.regularUnitPrice);
const netUnitPrice = Number(page.props.netUnitPrice);
const discountPercentage = Math.floor(100 - (netUnitPrice * 100 / regularUnitPrice)).toFixed(0);
const maxQuantity = Number(page.props.maxQuantity);
const maxDobTimestamp = Number(page.props.maxDobTimestamp);

const currencyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

const maxDob = new Date();
maxDob.setTime((maxDobTimestamp || (new Date().getTime() - 18 * 365 * 24 * 60 * 60)) * 1_000);
maxDob.setHours(0, 0, 0, 0);

const quantityOptions = Array.from(
    { length: maxQuantity },
    (_, i) => ({ value: i + 1, label: `${i + 1} chèque${i > 0 ? 's' : ''} d\'une valeur de 20 €` })
);

const pickupDates = ref([]);
const pickupDateHours = ref(new Map());
const pickupDisabledDates = ref([]);
const minPickupDate = ref(null);
const maxPickupDate = ref(null);

const applyPickupTimeSlots = (slots) => {
  pickupDates.value = [];
  pickupDateHours.value = new Map();
  minPickupDate.value = null;
  maxPickupDate.value = null
  pickupDisabledDates.value = [];

  for (const [ date, hours ] of Object.entries(slots)) {
    const matches = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (matches) {
      const [ , year, month, day ] = matches;
      const pickupDate = new Date(year, month - 1, day);

      pickupDates.value.push(pickupDate);
      pickupDateHours.value.set(pickupDate.getTime(), hours);

      if (!minPickupDate.value || (pickupDate < minPickupDate.value)) {
        minPickupDate.value = pickupDate;
      }

      if (!maxPickupDate.value || (pickupDate > maxPickupDate.value)) {
        maxPickupDate.value = pickupDate;
      }
    }
  }

  let checkedDate = new Date(minPickupDate.value);

  while (checkedDate <= maxPickupDate.value) {
    if (!pickupDateHours.value.has(checkedDate.getTime())) {
      pickupDisabledDates.value.push(checkedDate);
    }

    checkedDate = new Date(checkedDate.getTime() + 24 * 60 * 60 * 1000);
  }
};

applyPickupTimeSlots(page.props.pickupTimeSlots);

const getParsedDobString = (dob) => {
  if (!dob) {
    return null;
  }

  const matches = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (matches) {
    const [ , day, month, year ] = matches;

    return `${year}-${month}-${day}`;
  }

  return null;
};

const schema = yup.object({
  email:
      yup
          .string()
          .required()
          .matches(
              /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              ({ label }) => `${label} doit être une adresse valide`
          )
          .label('Adresse e-mail'),
  email_confirmation:
      yup
          .string()
          .when([], {
            is: () => !!isExistingUser.value,
            then: (s) => s.notRequired(),
            otherwise: (s) => s.required().oneOf([ yup.ref('email') ], 'Les adresses e-mail ne correspondent pas'),
          })
          .label('Confirmation de l\'adresse e-mail'),
  firstname:
      yup
          .string()
          .required()
          .label('Prénom'),
  lastname:
      yup
          .string()
          .required()
          .label('Nom'),
  phone:
      yup
          .string()
          .required()
          .matches(/^0\d(\s\d{2}){4}$/, ({ label }) => `${label} doit être un numéro de téléphone valide`)
          .label('Téléphone'),
  address:
      yup
          .string()
          .required()
          .label('Adresse'),
  postcode:
      yup
          .string()
          .required()
          .matches(/^\d{5}$/, ({ label }) => `${label} doit être un code postal valide`)
          .label('Code postal'),
  city:
      yup
          .string()
          .required()
          .label('Ville'),
  dob:
      yup
          .string()
          .required()
          .test({
            name: 'valid-date',
            test: function (value) {
              const matches = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

              if (matches) {
                const [ , day, month, year ] = matches;

                try {
                  const dob = new Date(year, month - 1, day);
                  dob.setHours(0, 0, 0, 0);

                  // Date support overflows by default. We don't.
                  if (
                      (dob.getDate() === Number(day))
                      && (dob.getMonth() === Number(month) - 1)
                      && (dob.getFullYear() === Number(year))
                  ) {
                    return (dob.getTime() <= maxDob.getTime())
                        ? true
                        : this.createError({ message: 'Vous devez avoir plus de 18 ans pour pouvoir passer commande' });
                  }
                } catch (error) {
                  // Invalid date.
                }
              }

              return this.createError({ message: '${label} doit être une date de naissance valide' });
            },
          })
          .label('Date de naissance'),
  quantity:
      yup
          .number()
          .required()
          .min(1)
          .max(maxQuantity)
          .label('Quantité'),
  pickup_date:
      yup
          .date()
          .required()
          .test(
              'isAvailablePickupDate',
              'La date de retrait n\'est pas disponible',
              (date) => pickupDateHours.value.has(date.getTime())
          )
          .label('Date de retrait'),
  pickup_hour:
      yup
          .number()
          .when(
              'pickup_date',
              ([ date ], s) => {
                const availableHours = date && pickupDateHours.value.get(date.getTime());

                return (!availableHours || (availableHours.length === 0))
                    ? s.notRequired()
                    : s.required().oneOf(availableHours)
              }
          )
          .label('Heure de retrait'),
  gtc:
      yup
          .boolean()
          .required()
          .default(false)
          .oneOf([ true ], 'Vous devez accepter les conditions générales de vente')
          .label('CGV'),
});

const pickupHourOptions = ref([]);

const refreshPickupHourOptions = () => {
  if (pickupDate.value) {
    const dateHours = pickupDateHours.value.get(pickupDate.value.getTime());

    if (dateHours) {
      pickupHourOptions.value = dateHours.map((hour) => ({
        value: hour,
        label: `${hour}h - ${hour + 1}h`,
      }));

      if (pickupHour.value && !pickupHourOptions.value.find(({ value }) => value === pickupHour.value)) {
        pickupHour.value = null;
      }
    }
  }
};

const { defineField, errors, handleSubmit } = useForm({
  validationSchema: schema,
});

const [ email, emailAttrs ] = defineField('email');
const [ emailConfirmation, emailConfirmationAttrs ] = defineField('email_confirmation');

const [ firstname, firstnameAttrs ] = defineField('firstname');
const [ lastname, lastnameAttrs ] = defineField('lastname');
const [ phone, phoneAttrs ] = defineField('phone');
const [ address, addressAttrs ] = defineField('address');
const [ postcode, postcodeAttrs ] = defineField('postcode');
const [ city, cityAttrs ] = defineField('city');
const [ dob, dobAttrs ] = defineField('dob');

const [ quantity, quantityAttrs ] = defineField('quantity');
const [ pickupDate, pickupDateAttrs ] = defineField('pickup_date');
const [ pickupHour, pickupHourAttrs ] = defineField('pickup_hour');

const [ gtc, gtcAttrs ] = defineField('gtc');
const [ newsletterOptIn, newsletterOptInAttrs ] = defineField('newsletter_optin');

watch(() => pickupDate.value, () => refreshPickupHourOptions());

const gtcModalVisible = ref(false);
// const pickupSlipModalVisible = ref(false);

const showGtcModal = (event) => {
  event.stopPropagation();
  event.preventDefault();
  gtcModalVisible.value = true;
};

const isRefreshingPickupTimeSlots = ref(false);

const refreshPickupTimeSlots = () => {
  // todo not needed probably (/ and unexisting)
  const payload = {
    _token: page.props.csrf_token,
  };

  isRefreshingPickupTimeSlots.value = true;

  axios.post(route('order.pickup-time-slots'), payload)
       .then((response) => {
         if (response.data) {
           applyPickupTimeSlots(response.data);
           refreshPickupHourOptions();
         }
       })
       .catch(() => {
         toast.add({
           severity: 'error',
           summary: 'Erreur de traitement',
           detail: 'Impossible de rafraîchir les créneaux de retrait. Veuillez rafraîchir la page pour mettre à jour les créneaux disponibles.',
         });
       })
       .finally(() => {
         isRefreshingPickupTimeSlots.value = false;
       });
};

const isSubmitting = ref(false);

const onSubmit = handleSubmit(values => {
  if (isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;

  const payload = {
    ...values,
    // todo not needed probably (/ and unexisting)
    _token: page.props.csrf_token,
    dob: getParsedDobString(values.dob),
    pickup_date: (
        values.pickup_date.getFullYear()
        + '-'
        + (values.pickup_date.getMonth() + 1)
        + '-'
        + values.pickup_date.getDate()
    ),
  };

  axios.post(route('order.submit'), payload)
       .then((response) => {
         if (response.data?.submitted) {
           if (response.data.payment_url) {
             window.location.href = response.data.payment_url;
           } else {
             window.location.href = route('order.success');
           }
         } else if (response.data?.reload) {
           window.location.reload();
         } else {
           throw response;
         }
       })
       .catch((error) => {
         isSubmitting.value = false;

         if (error.response?.data?.errors) {
           const messages = Object.values(error.response.data.errors).flat();

           if (
               error.response.data.errors.pickup_date
               || error.response.data.errors.pickup_hour
           ) {
             refreshPickupTimeSlots();
           }

           toast.add({
             severity: 'error',
             summary: 'Erreur de traitement',
             detail: messages.join('\n\n'),
           });

         } else {
           toast.add({
             severity: 'error',
             summary: 'Erreur de traitement',
             detail: 'Une erreur est survenue lors de l\'enregistrement de la commande. Veuillez réessayer ultérieurement.',
           });
         }
       });
});
</script>

<template>
  <Head title="Chèques cadeaux"/>

  <div class="min-h-full">
    <header class="pt-8 pb-4 flex justify-center">
      <ApplicationLogo class="w-72"/>

      <!--
      <nav class="mx-4 flex flex-1 justify-end">
        <Button @click="pickupSlipModalVisible = true" class="flex-col gap-0">
          <nobr>Déjà commandé ?</nobr>
          <nobr>&gt; Mon bon de retrait</nobr>
        </Button>
      </nav>
      -->
    </header>

    <main
        class="relative max-w-2xl px-4 py-4 sm:px-6 lg:px-8 flex flex-col justify-center sm:items-center mx-auto size-full">
      <div class="text-center pb-4 px-4 sm:px-6 lg:px-8 size-full">
        <h1 class="text-xl sm:text-2xl text-primary uppercase font-semibold">
          Commande de chèques cadeaux
          <nobr v-if="discountPercentage > 0">en promotion</nobr>
        </h1>

        <div class="my-4 flex flex-col gap-2">
          <p>
            Achetez vos chèques cadeaux en quelques étapes simples :
          </p>

          <ul class="ml-6 list-disc list-outside text-left">
            <li class="mt-2">
              Remplissez les informations ci-dessous
            </li>

            <li class="mt-2">
              Choisissez le nombre de chèques d'une valeur de
              <span class="font-bold">{{ currencyFormatter.format(regularUnitPrice) }}</span>
              chacun
              <nobr>(<span class="font-bold">{{ maxQuantity }}</span> maximum)</nobr>
            </li>

            <li class="mt-2">
              Sélectionnez le jour et le créneau horaire souhaité de retrait à la Conciergerie Mon Shopping C'est Calais
              (une pièce d'identité sera demandée lors du retrait de votre commande)
            </li>
          </ul>
        </div>

        <form>
          <div class="flex flex-col gap-6 mt-10">
            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputText
                    id="email"
                    fluid
                    v-model="email"
                    v-bind="emailAttrs"
                    :invalid="!!errors.email"
                />

                <label for="email">Adresse e-mail</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.email">
                {{ errors.email }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputText
                    id="emailConfirmation"
                    fluid
                    v-model="emailConfirmation"
                    v-bind="emailConfirmationAttrs"
                    :invalid="!!errors.email_confirmation"
                />

                <label for="email">Confirmation de l'adresse e-mail</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.email_confirmation">
                {{ errors.email_confirmation }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputText
                    id="firstname"
                    fluid
                    v-model="firstname"
                    v-bind="firstnameAttrs"
                    :invalid="!!errors.firstname"
                />

                <label for="firstname">Prénom</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.firstname">
                {{ errors.firstname }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputText
                    id="lastname"
                    fluid
                    v-model="lastname"
                    v-bind="lastnameAttrs"
                    :invalid="!!errors.lastname"
                />

                <label for="lastname">Nom</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.lastname">
                {{ errors.lastname }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputMask
                    id="phone"
                    fluid
                    v-model="phone"
                    v-bind="phoneAttrs"
                    :invalid="!!errors.phone"
                    mask="99 99 99 99 99"
                    :autoClear="false"
                    pt:root:inputmode="numeric"
                />

                <label for="address">Téléphone</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.phone">
                {{ errors.phone }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputText
                    id="address"
                    fluid
                    v-model="address"
                    v-bind="addressAttrs"
                    :invalid="!!errors.address"
                />

                <label for="address">Adresse</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.address">
                {{ errors.address }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputMask
                    id="postcode"
                    fluid
                    v-model="postcode"
                    v-bind="postcodeAttrs"
                    :invalid="!!errors.postcode"
                    mask="99999"
                    :autoClear="false"
                    pt:root:inputmode="numeric"
                />

                <label for="postcode">Code postal</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.postcode">
                {{ errors.postcode }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputText
                    id="city"
                    fluid
                    v-model="city"
                    v-bind="cityAttrs"
                    :invalid="!!errors.city"
                />

                <label for="city">Ville</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.city">
                {{ errors.city }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <InputMask
                    id="dob"
                    fluid
                    v-model="dob"
                    v-bind="dobAttrs"
                    :invalid="!!errors.dob"
                    mask="99/99/9999"
                    :autoClear="false"
                    pt:root:inputmode="numeric"
                />

                <label for="dob">Date de naissance</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.dob">
                {{ errors.dob }}
              </small>

              <small>
                <span class="font-bold">Format : JJ/MM/AAAA</span>
              </small>

              <small>
                <span class="font-bold">Vous devez avoir plus de 18 ans pour pouvoir passer commande.</span>
              </small>
            </div>

            <h3 class="text-xl font-semibold text-slate-400">
              Votre commande
            </h3>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <Select
                    id="quantity"
                    fluid
                    v-model="quantity"
                    v-bind="quantityAttrs"
                    :invalid="!!errors.quantity"
                    :options="quantityOptions"
                    optionValue="value"
                    optionLabel="label"
                />

                <label for="quantity">Quantité</label>
              </FloatLabel>

              <small>
                <span class="font-bold">Maximum de {{ maxQuantity }} chèques par personne majeure.</span>
              </small>

              <small class="w-full text-center text-red-600" v-if="!!errors.quantity">
                {{ errors.quantity }}
              </small>
            </div>

            <div
                class="my-2 py-8 flex flex-row flex-wrap justify-center items-center gap-4 border-y-2 border-slate-200 sm:flex-nowrap sm:justify-end">
              <div class="flex-1 basis-full text-2xl text-center sm:basis-auto sm:text-left">
                Total :
              </div>

              <span class="line-through" v-if="(quantity > 0) && (discountPercentage > 0)">
                {{ currencyFormatter.format((quantity || 0) * regularUnitPrice) }}
              </span>

              <span class="px-2 bg-primary text-white text-sm rounded"
                    v-if="(quantity > 0) && (discountPercentage > 0)">
                -{{ discountPercentage }}%
              </span>

              <span class="text-4xl text-primary font-bold">
                {{ currencyFormatter.format((quantity || 0) * netUnitPrice) }}
              </span>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <DatePicker
                    id="pickupDate"
                    fluid
                    v-model="pickupDate"
                    v-bind="pickupDateAttrs"
                    :invalid="!!errors.pickup_date"
                    showIcon
                    dateFormat="dd/mm/yy"
                    :disabledDates="pickupDisabledDates"
                    :minDate="minPickupDate"
                    :maxDate="maxPickupDate"
                    appendTo="self"
                    :disabled="!minPickupDate || isRefreshingPickupTimeSlots"
                />

                <label for="pickupDate">Date de retrait</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.pickup_date">
                {{ errors.pickup_date }}
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <FloatLabel variant="on">
                <Select
                    id="quantity"
                    fluid
                    v-model="pickupHour"
                    v-bind="pickupHourAttrs"
                    :invalid="!!errors.pickup_hour"
                    :disabled="(pickupHourOptions.length === 0) || isRefreshingPickupTimeSlots"
                    :options="pickupHourOptions"
                    optionValue="value"
                    optionLabel="label"
                />

                <label for="quantity">Heure de retrait</label>
              </FloatLabel>

              <small class="w-full text-center text-red-600" v-if="!!errors.pickup_hour">
                {{ errors.pickup_hour }}
              </small>

              <small>
                <span class="font-bold">
                  Retrait à la Conciergerie Mon Shopping C'est Calais 
                  <br/>Centre Calais Coeur de Vie - 6 Bd Jacquard - Calais
                </span>
              </small>
            </div>

            <div class="relative flex flex-col gap-2">
              <div class="flex items-center">
                <Checkbox
                    v-model="gtc"
                    inputId="gtc"
                    name="gtc"
                    v-bind="gtcAttrs"
                    binary
                />

                <label for="gtc" class="ml-2">
                  J'accepte les
                  <a href="#" class="text-primary" @click="showGtcModal">conditions générales de vente</a>
                </label>
              </div>

              <small class="w-full text-center text-red-600" v-if="!!errors.gtc">
                {{ errors.gtc }}
              </small>
            </div>

            <div class="flex items-center">
              <Checkbox
                  v-model="newsletterOptIn"
                  inputId="newsletter_optin"
                  name="newsletter_optin"
                  v-bind="newsletterOptInAttrs"
                  binary
              />

              <label for="newsletter_optin" class="ml-2">
                Je souhaite recevoir la newsletter "Mon Shopping C’est Calais"
              </label>
            </div>

            <div>
              <Button
                  label="Commander"
                  class="w-full"
                  :loading="isSubmitting"
                  @click="onSubmit"
              />
            </div>
          </div>

          <div class="my-8 flex flex-col gap-2 italic">
            <p>
              <a href="https://monshoppingcestcalais.fr/media/wysiwyg/Cheques-cadeaux-liste-commercants-VN.pdf"
                 class="text-primary underline" target="_blank">
                Découvrez la liste des commerçants acceptant les chèques cadeaux
                <nobr>en cliquant ici</nobr>
              </a>
            </p>

            <p class="font-bold">Pour rappel :</p>

            <ul class="ml-6 list-disc list-outside">
              <li>
                Une pièce d'identité sera exigée lors du retrait de vos chèques cadeaux.
              </li>

              <li>
                Les chèques sont à retirer aux jour et heure choisis à la conciergerie Mon Shopping C'est Calais.
              </li>
            </ul>
          </div>
        </form>
      </div>

      <!-- todo if enabled again, move this to its own component -->
      <!--
      <Dialog
          v-model:visible="pickupSlipModalVisible"
          modal
          dismissableMask
          header="Mon bon de retrait"
          style="max-width: 45em; margin: 1.5em;"
      >
        <div class="flex flex-col gap-2">
          <p>
            <strong>Vous avez perdu votre bon de retrait ?</strong>
          </p>
          <p class="text-justify">
            Entrez l'adresse e-mail avec laquelle vous avez passé votre commande.
            <br/>Nous vous renverrons un lien vers votre bon de retrait.
          </p>
          <p>
            <strong>Vous ne recevez pas d'e-mail ?</strong>
          </p>
          <p>
            Contactez-nous par e-mail :
            <a href="mailto: cheque-cadeau@splcalais.fr" class="text-primary">cheque-cadeau@splcalais.fr</a>
            ou par téléphone :
            <strong>03 66 62 97 86</strong>.
            <br/>Nous ferons le nécessaire pour vous aider.
          </p>
          <div class="mt-4 relative flex flex-col gap-2">
            <FloatLabel variant="on">
              <InputText
                  id="slip_email"
                  fluid
              />

              <label for="email">Adresse e-mail</label>
            </FloatLabel>
          </div>
        </div>
        <template #footer>
          <div class="mt-4">
            <Button label="Envoyer" severity="primary" @click="pickupSlipModalVisible = false" autofocus/>
          </div>
        </template>
      </Dialog>
      -->

      <Dialog v-model:visible="gtcModalVisible" modal header="Conditions générales de vente"
              style="max-width: 60em; margin: 1.5em;">
        <div class="gtc-text">
          <Gtc/>
        </div>

        <template #footer>
          <div class="mt-4">
            <Button label="OK" severity="primary" @click="gtcModalVisible = false" autofocus/>
          </div>
        </template>
      </Dialog>

      <Toast/>
    </main>
  </div>
</template>
